// @ts-check

import { computed } from 'vue';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/vue-query';

import { queryTemplates, templateListKeys } from '@/queries/templates';

/** @typedef {import('@/store/modules/bulkSend/models/Templates').TListTemplate} TListTemplate */

/**
 * Custom composable function for fetching infinite templates based on a search query.
 *
 * @param {import('vue').Ref<string>} query - The search query for filtering templates.
 * @param {boolean|import('vue').Ref<boolean>} [enabled=true] - Whether the query should be enabled.
 * @param {typeof queryTemplates} [queryFn=queryTemplates] - The query function to use for fetching templates.
 */
export function useInfiniteTemplates(query, enabled = true, queryFn = queryTemplates) {
  const queryKey = computed(() =>
    (query.value ? templateListKeys.search(query.value) : templateListKeys.all));

  const {
    isPending,
    isFetching,
    isFetchingNextPage,
    isPlaceholderData,
    isError,
    hasNextPage,
    data,
    error,
    refetch,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey,
    queryFn,
    enabled,
    initialPageParam: 1,
    getNextPageParam: lastPage =>
      (lastPage.meta.current_page < lastPage.meta.last_page
        ? lastPage.meta.current_page + 1
        : undefined),
    placeholderData: keepPreviousData,
    gcTime: 0,
  });

  /** @type {import('vue').ComputedRef<TListTemplate[]|undefined>} */
  const templates = computed(() =>
    data.value?.pages.flatMap(page => page.templates));

  const totalTemplates = computed(() => data.value?.pages[0]?.meta.total || 0);

  function fetchNext() {
    if (!isFetching.value && hasNextPage.value) {
      fetchNextPage();
    }
  }

  return {
    templates,
    totalTemplates,
    fetchNextTemplates: fetchNext,
    refetchTemplates: refetch,
    hasNextTemplates: hasNextPage,
    isPendingTemplates: isPending,
    isFetchingTemplates: isFetching,
    isFetchingNextTemplates: isFetchingNextPage,
    isPlaceholderTemplates: isPlaceholderData,
    isErrorTemplates: isError,
    errorTemplates: error,
  };
}
